import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStateDraft } from "@/hooks/useStateDraft";
import { buildDraftKey, useFormDraftStore } from "@/store/formDraftStore";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useUserStore } from "@/store/userStore";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, ArrowLeft } from "lucide-react";
import { uploadProfilePhoto, updateUserProfile, getUsersEmailByUserIds } from "@/services/userProfileService";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { getGyms } from "@/services/edgeFunctionWrappers/settingsManagementWrapper";
import { invokeWithRetry } from '@/services/edgeFunctionWrappers/common/invokeWithRetry';
import { getUserGymId } from '@/utils/gymAuth';
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CoachStatsTab from "@/pages/admin/components/coach-stats/CoachStatsTab";

type Gym = { id: string; name: string };

const CoachDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation(["coach", "common"]);
  const { role } = useUserStore();
  const navigate = useNavigate();

  const [coach, setCoach] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [loadingGyms, setLoadingGyms] = useState(false);

  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // form
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    emergency_contact: "",
    birth_date: undefined as Date | undefined,
    notes: "",
    // champs pertinents côté coach
    specialization: "",
    bio: "",
    // lecture seule
    email: "",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Brouillon persisté — actif seulement en mode édition (hors édition les
  // champs sont désactivés : sauvegarder reviendrait à figer un instantané
  // serveur qui masquerait ensuite des données fraîches pendant 24 h).
  // email exclu (lecture seule) ; birth_date revit en Date (le JSON du
  // stockage l'a transformée en chaîne, format() lèverait sinon).
  const { hasRestoredDraftRef, clearDraft } = useStateDraft(
    `coach-details:${id}`,
    formData,
    setFormData,
    {
      enabled: isEditing,
      exclude: ["email"],
      isEmpty: (d) =>
        !d.first_name && !d.last_name && !d.phone && !d.address &&
        !d.emergency_contact && !d.notes && !d.specialization && !d.bio,
      revive: (d) => ({
        ...d,
        birth_date: d.birth_date ? new Date(d.birth_date as string) : undefined,
      }),
    }
  );

  // Au montage : si un brouillon d'édition existe pour ce coach, rouvrir la
  // page directement EN mode édition — le hook (enabled: isEditing) restaure
  // alors le brouillon par-dessus les valeurs chargées.
  useEffect(() => {
    if (!id) return;
    const { gymId: gid, user } = useUserStore.getState();
    const draft = useFormDraftStore
      .getState()
      .getDraft(buildDraftKey(`coach-details:${id}`, gid, user?.id ?? null));
    if (draft) setIsEditing(true);
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCoachDetails(id);
    }
  }, [id]);

  useEffect(() => {
    // charger la liste des salles via edge function
    (async () => {
      setLoadingGyms(true);
      try {
        const gymId = getUserGymId();
        if (gymId) {
          const gymsData = await getGyms(gymId);
          setGyms(gymsData);
        }
      } catch (error) {
        console.error("fetch gyms error:", error);
        toast.error(t("fetchGymsError", { ns: "coach", defaultValue: "Impossible de charger les salles." }));
      } finally {
        setLoadingGyms(false);
      }
    })();
  }, []);

  const fetchCoachDetails = async (coachId: string) => {
    try {
      const gymId = getUserGymId();
      if (!gymId) throw new Error('No gym selected');

      // Fetch coach via edge function
      const { data: coachResponse, error: coachError } = await invokeWithRetry('coaches-management', {
        action: 'get_coach',
        gym_id: gymId,
        params: { id: coachId }
      });

      if (coachError) throw coachError;
      const coachData = coachResponse?.coach;
      if (!coachData) throw new Error('Coach not found');
      
      setCoach(coachData);

      // profil utilisateur lié
      if (coachData?.user_id) {
        const { data: profileData, error: profileError } = await getUserProfile(coachData.user_id);
        if (profileError) throw profileError;

        setUserProfile(profileData);

        if (profileData?.profile_photo_url) {
          setPreviewPhotoUrl(profileData.profile_photo_url);
        }

        const emailMap = await getUsersEmailByUserIds([coachData.user_id]);

        // Un brouillon restauré prime sur les valeurs serveur — mais l'email
        // (exclu du brouillon, lecture seule) doit quand même arriver.
        if (hasRestoredDraftRef.current) {
          setFormData((prev) => ({ ...prev, email: emailMap[coachData.user_id] ?? "" }));
          return;
        }

        setFormData({
          first_name: profileData?.first_name ?? "",
          last_name: profileData?.last_name ?? "",
          phone: profileData?.phone ?? "",
          address: profileData?.address ?? "",
          emergency_contact: profileData?.emergency_contact ?? "",
          birth_date: profileData?.birth_date ? new Date(profileData.birth_date) : undefined,
          notes: profileData?.notes ?? "",
          specialization: coachData?.specialization ?? "",
          bio: coachData?.bio ?? "",
          email: emailMap[coachData.user_id] ?? "",
        });
      }
    } catch (error) {
      console.error("Error fetching coach details:", error);
      toast.error(t("fetchError", { ns: "coach", defaultValue: "Erreur de chargement." }));
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };
  const handleTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };
  const handleDateChange = (date?: Date) => {
    setFormData((s) => ({ ...s, birth_date: date }));
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    setIsUploading(true);
    const file = event.target.files[0];

    try {
      const previewURL = URL.createObjectURL(file);
      setPreviewPhotoUrl(previewURL);

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error(t("authRequired", { ns: "coach", defaultValue: "Authentification requise." }));
        URL.revokeObjectURL(previewURL);
        setPreviewPhotoUrl(userProfile?.profile_photo_url || null);
        return;
      }

      // NOTE : upload avec l'ID de session (inchangé)
      const photoUrl = await uploadProfilePhoto(sessionData.session.user.id, file);
      if (!photoUrl) {
        toast.error(t("photoUploadError", { ns: "coach", defaultValue: "Échec du téléversement." }));
        setPreviewPhotoUrl(userProfile?.profile_photo_url || null);
        return;
      }

      URL.revokeObjectURL(previewURL);

      const success = await updateUserProfile(coach.user_id, { profile_photo_url: photoUrl });
      if (!success) {
        toast.error(t("photoUploadError", { ns: "coach" }));
        setPreviewPhotoUrl(userProfile?.profile_photo_url || null);
        return;
      }

      setUserProfile((u: any) => ({ ...(u ?? {}), profile_photo_url: photoUrl }));
      setPreviewPhotoUrl(photoUrl);
      toast.success(t("photoUpdated", { ns: "coach", defaultValue: "Photo mise à jour." }));

      if (id) fetchCoachDetails(id);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(t("photoUploadError", { ns: "coach" }));
      setPreviewPhotoUrl(userProfile?.profile_photo_url || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // 1) user_profiles
      const profileUpdate: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        address: formData.address,
        emergency_contact: formData.emergency_contact,
        birth_date: formData.birth_date ? formData.birth_date.toISOString() : null,
        notes: formData.notes,
      };

      const okProfile = await updateUserProfile(coach.user_id, profileUpdate);
      if (!okProfile) throw new Error("profile update failed");

      // 2) coaches (spécialité / bio si présents) via edge function
      const gymId = getUserGymId();
      if (!gymId) throw new Error('No gym selected');

      const updates: any = {};
      updates.specialization = formData.specialization ?? "";
      if (typeof formData.bio === "string") updates.bio = formData.bio;

      const { error: coachErr } = await invokeWithRetry('coaches-management', {
        action: 'update_coach',
        gym_id: gymId,
        params: { id, updates }
      });

      if (coachErr) throw coachErr;

      toast.success(t("updateSuccess", { ns: "coach", defaultValue: "Enregistré." }));
      clearDraft();
      hasRestoredDraftRef.current = false; // le refetch doit reprendre la main
      setIsEditing(false);
      if (id) fetchCoachDetails(id);
    } catch (error) {
      console.error("Error updating coach details:", error);
      toast.error(t("updateError", { ns: "coach", defaultValue: "Erreur lors de l'enregistrement." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => navigate("/admin/coaches");

const fullName = `${formData.first_name || "—"} ${formData.last_name || ""}`.trim();

if (!coach || !userProfile) {
  return <div className="p-4">{t("loading", { ns: "coach", defaultValue: "Chargement..." })}</div>;
}


  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleGoBack}
              className="h-8 w-8"
              aria-label={t("common:back")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {fullName || t("title", { ns: "coach", defaultValue: "Coach" })}
              </h1>
              <p className="text-muted-foreground">
                {t("subtitle", { ns: "coach", defaultValue: "Fiche coach et informations professionnelles." })}
              </p>
            </div>
          </div>

          
        </div>

        <Tabs defaultValue="infos" className="w-full">
          <TabsList>
            <TabsTrigger value="infos">{t("infos", { ns: "coach", defaultValue: "Infos" })}</TabsTrigger>
            <TabsTrigger value="stats">{t("stats", { ns: "coach", defaultValue: "Statistiques" })}</TabsTrigger>
          </TabsList>
          <TabsContent value="infos" className="mt-4">
        {/* Carte principale */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-20 w-20">
                  {previewPhotoUrl ? (
                    <AvatarImage src={previewPhotoUrl} alt="Profile" />
                  ) : (
                    <AvatarFallback>
                      {formData.first_name && formData.last_name
                        ? `${formData.first_name.charAt(0)}${formData.last_name.charAt(0)}`
                        : "?"}
                    </AvatarFallback>
                  )}
                </Avatar>
                {isEditing && (
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute -bottom-2 -right-2 rounded-full h-8 w-8"
                    onClick={triggerFileInput}
                    disabled={isUploading}
                    title={t("changePhoto", { ns: "coach", defaultValue: "Changer la photo" })}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  id="profilePhoto"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={!isEditing || isUploading}
                />
              </div>

            </div>
          </CardHeader>
          <Separator />
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
            {/* Identité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t("firstName", { ns: "common", defaultValue: "Prénom" })}</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInput}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t("lastName", { ns: "common", defaultValue: "Nom" })}</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInput}
                  disabled={!isEditing}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">{t("email", { ns: "common", defaultValue: "Email" })}</Label>
                <Input id="email" name="email" value={formData.email} disabled readOnly />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">{t("phone", { ns: "common", defaultValue: "Téléphone" })}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInput}
                  disabled={!isEditing}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">{t("address", { ns: "common", defaultValue: "Adresse" })}</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInput}
                  disabled={!isEditing}
                />
              </div>
            </div>

            {/* Naissance + Rôle + Salle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="birthDate">{t("birthDate", { ns: "coach", defaultValue: "Date de naissance" })}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.birth_date && "text-muted-foreground"
                      )}
                      disabled={!isEditing}
                    >
                      {formData.birth_date ? format(formData.birth_date, "PPP") : <span>{t("pickDate", { ns: "coach", defaultValue: "Choisir une date" })}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={formData.birth_date}
                      onSelect={handleDateChange}
                      disabled={!isEditing}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

            </div>

            {/* Informations pro (coaches table) */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="specialization">{t("specialty", { ns: "coach", defaultValue: "Spécialité" })}</Label>
                <Input
                  id="specialization"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInput}
                  disabled={!isEditing}
                  placeholder={t("specialtyPlaceholder", { ns: "coach", defaultValue: "CrossFit, Yoga, Fitness…" })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="bio">{t("bio", { ns: "coach", defaultValue: "Bio / Présentation" })}</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleTextarea}
                  disabled={!isEditing}
                  placeholder={t("bioPlaceholder", { ns: "coach", defaultValue: "Parlez de votre expérience, diplômes, approche…" })}
                  rows={4}
                />
              </div>
            </div>

            {/* Notes internes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">{t("notes", { ns: "common", defaultValue: "Notes" })}</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleTextarea}
                disabled={!isEditing}
                rows={4}
              />
            </div>
          </CardContent>

          <Separator />

          <CardContent className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
            {role === "admin" && (
              <Button
                onClick={() => {
                  // Quitter le mode édition = annulation explicite : brouillon
                  // jeté et valeurs serveur rechargées.
                  if (isEditing) {
                    clearDraft();
                    hasRestoredDraftRef.current = false;
                    if (id) fetchCoachDetails(id);
                  }
                  setIsEditing((v) => !v);
                }}
                disabled={isSubmitting}
                variant="secondary"
              >
                {isEditing ? t("cancel", { ns: "common", defaultValue: "Annuler" }) : t("edit", { ns: "common", defaultValue: "Modifier" })}
              </Button>
            )}
            {isEditing && (
              <Button onClick={handleSubmit} disabled={isSubmitting || isUploading}>
                {isSubmitting ? t("saving", { ns: "common", defaultValue: "Enregistrement..." }) : t("save", { ns: "common", defaultValue: "Enregistrer" })}
              </Button>
            )}
          </CardContent>
        </Card>
          </TabsContent>
          <TabsContent value="stats" className="mt-4">
            <CoachStatsTab coachUserId={coach?.user_id} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CoachDetails;
